import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Types
interface Conference {
  // Add conference properties based on your API response
  [key: string]: any;
}

interface StreamInfo {
  conference: string;
  transfer_conference: string;
}

interface AppState {
  loading: boolean;
  conferenceList: Conference[];
  isLoggedIn: boolean;
  hasVerifiedPin: boolean;
  hasVerifiedPasscode: boolean;
  phoneNumber: string;
  conference: string;
  transferConference: string;
  passcodeRetries: number | null;
  pinRetries: number | null;
  selfRegisterOpen: boolean;
  profileCompleted: boolean;
  registerFailure: boolean;
  language: string;
  showUsageTerms: boolean;
  selfRegisterNoAccess: boolean;
}

// Action types
type Action =
  | { type: 'SHOW_USAGE_TERMS'; showUsageTerms: boolean }
  | { type: 'LOADING' }
  | { type: 'CONFERENCE_LIST'; conferenceList: Conference[] }
  | {
      type: 'LOGIN';
      isLoggedIn: boolean;
      pinVerified: boolean;
      passcodeVerified: boolean;
      profileCompleted: boolean;
    }
  | { type: 'VERIFIED_PASSCODE'; passcodeVerified: boolean }
  | { type: 'VERIFIED_PASSCODE_RETRIES'; retries: number }
  | { type: 'VERIFIED_PIN'; pinVerified: boolean; profileCompleted: boolean }
  | { type: 'VERIFIED_PIN_RETRIES'; retries: number }
  | { type: 'LOGOUT' }
  | { type: 'STREAM_INFO'; streamInfo: StreamInfo }
  | { type: 'SELF_REGISTER_OPEN'; selfRegisterOpen: boolean }
  | { type: 'REGISTER_COMPLETE'; profileCompleted: boolean }
  | { type: 'REGISTER_FAILURE' }
  | { type: 'FORGET_ME' }
  | { type: 'SET_LANGUAGE'; language: string }
  | { type: 'SELF_REGISTER_NO_ACCESS' };

const initialState: AppState = {
  loading: false,
  conferenceList: [],
  isLoggedIn: false,
  hasVerifiedPin: false,
  hasVerifiedPasscode: false,
  phoneNumber: '',
  conference: '',
  transferConference: '',
  passcodeRetries: null,
  pinRetries: null,
  selfRegisterOpen: false,
  profileCompleted: false,
  registerFailure: false,
  language: 'NO',
  showUsageTerms: false,
  selfRegisterNoAccess: false,
};

const reducer = (state: AppState, action: Action): AppState => {
  switch (action.type) {
    case 'SHOW_USAGE_TERMS':
      return { ...state, showUsageTerms: action.showUsageTerms };
    case 'LOADING':
      return { ...state, loading: true };
    case 'CONFERENCE_LIST':
      return { ...state, loading: false, conferenceList: action.conferenceList };
    case 'LOGIN':
      return {
        ...state,
        loading: false,
        isLoggedIn: action.isLoggedIn,
        hasVerifiedPin: action.pinVerified,
        hasVerifiedPasscode: action.passcodeVerified,
        profileCompleted: action.profileCompleted,
      };
    case 'VERIFIED_PASSCODE':
      return { ...state, loading: false, hasVerifiedPasscode: action.passcodeVerified };
    case 'VERIFIED_PASSCODE_RETRIES':
      return { ...state, loading: false, passcodeRetries: action.retries };
    case 'VERIFIED_PIN':
      return {
        ...state,
        loading: false,
        hasVerifiedPin: action.pinVerified,
        profileCompleted: action.profileCompleted,
      };
    case 'VERIFIED_PIN_RETRIES':
      return { ...state, loading: false, pinRetries: action.retries };
    case 'LOGOUT':
      return { ...initialState, conferenceList: state.conferenceList };
    case 'STREAM_INFO':
      return {
        ...state,
        loading: false,
        conference: action.streamInfo.conference,
        transferConference: action.streamInfo.transfer_conference,
      };
    case 'SELF_REGISTER_OPEN':
      return { ...state, selfRegisterOpen: action.selfRegisterOpen };
    case 'REGISTER_COMPLETE':
      return { ...state, profileCompleted: action.profileCompleted };
    case 'REGISTER_FAILURE':
      return { ...state, loading: false, registerFailure: true };
    case 'FORGET_ME':
      return initialState;
    case 'SET_LANGUAGE':
      return { ...state, language: action.language };
    case 'SELF_REGISTER_NO_ACCESS':
      return { ...state, selfRegisterNoAccess: true, loading: false };
    default:
      return state;
  }
};

// Context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<Action>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider
export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>;
};

// Hook
export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// API Actions (these are now regular async functions that take dispatch as parameter)
export const getConferenceList = async (dispatch: React.Dispatch<Action>) => {
  dispatch({ type: 'LOADING' });
  const response = await fetch('/api/conference', {
    credentials: 'include',
  });
  const data = await response.json();
  dispatch({ type: 'CONFERENCE_LIST', conferenceList: data });
};

export const getStreamInfo = async (dispatch: React.Dispatch<Action>) => {
  dispatch({ type: 'LOADING' });
  const response = await fetch('/api/streaminfo', {
    credentials: 'include',
  });
  if (response.status !== 200) {
    logout(dispatch);
  } else {
    const data = await response.json();
    dispatch({ type: 'STREAM_INFO', streamInfo: data });
  }
};

export const fetchLoggedIn = async (dispatch: React.Dispatch<Action>) => {
  dispatch({ type: 'LOADING' });
  const response = await fetch('/api/login', {
    credentials: 'include',
  });
  const data = await response.json();
  dispatch({
    type: 'LOGIN',
    isLoggedIn: data.authenticated,
    pinVerified: data.verified_pin,
    passcodeVerified: data.verified_passcode,
    profileCompleted: data.profile_completed,
  });
  dispatch({ type: 'SELF_REGISTER_OPEN', selfRegisterOpen: data.self_register_open });
};

export const verifyPasscode = async (
  passcode: string ,
  dispatch: React.Dispatch<Action>
) => {
  dispatch({ type: 'LOADING' });
  try {
    const response = await fetch('/api/passcode', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(passcode),
    });

    console.log(response.status, response.statusText);

    if (response.status === 403) {
      console.log('ELF');
      dispatch({ type: 'SELF_REGISTER_NO_ACCESS' });
      return;
    }
    if (response.status === 401) {
      const data = await response.json();
      dispatch({ type: 'VERIFIED_PASSCODE_RETRIES', retries: data });
      return;
    }

    if (response.status !== 200) {
      throw new Error(response.status.toString());
    }

    const data = await response.json();

    if (data === true) {
      console.log('PIN verified', data);
      // Fetch auth status to get updated verified_pin status
      // (backend may have auto-verified PIN via cookie)
      await fetchLoggedIn(dispatch);
    } else {
      dispatch({ type: 'VERIFIED_PASSCODE_RETRIES', retries: data });
    }
  } catch (e) {
    logout(dispatch);
  }
};

export const verifyPin = async (pin: string, dispatch: React.Dispatch<Action>) => {
  try {
    dispatch({ type: 'LOADING' });

    const response = await fetch('/api/pin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pin),
    });
    if (response.status !== 200) {
      throw new Error(response.status.toString());
    }

    const data = await response.json();
    console.log('verifyPin returned', data);

    if (data === true) {
      dispatch({ type: 'VERIFIED_PIN', pinVerified: data, profileCompleted: true });
    } else if (data === 'self-register') {
      dispatch({ type: 'VERIFIED_PIN', pinVerified: true, profileCompleted: false });
    } else {
      dispatch({ type: 'VERIFIED_PIN_RETRIES', retries: data });
    }
  } catch (e) {
    logout(dispatch);
  }
};

export const setCurrentLanguage = async (language: string, dispatch: React.Dispatch<Action>) => {
  if (language === 'SE') {
    dispatch({ type: 'SET_LANGUAGE', language: language });
  } else {
    dispatch({ type: 'SET_LANGUAGE', language: 'NO' });
  }
};

export const registerProfile = async (
  name: { name: string },
  dispatch: React.Dispatch<Action>
) => {
  try {
    dispatch({ type: 'LOADING' });

    const response = await fetch('/api/register', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(name.name),
    });

    if (response.status !== 200) {
      throw new Error(response.status.toString());
    }

    const data = await response.json();

    if (data === true) {
      dispatch({ type: 'REGISTER_COMPLETE', profileCompleted: true });
    } else {
      dispatch({ type: 'REGISTER_FAILURE' });
    }
  } catch (e) {
    logout(dispatch);
  }
};

export const login = async (
  phoneNumber: string,
  conference: string,
  dispatch: React.Dispatch<Action>
) => {
  dispatch({ type: 'LOADING' });
  const response = await fetch('/api/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mobil: phoneNumber,
      samlingshus: conference,
    }),
  });

  const data = await response.json();

  dispatch({
    type: 'LOGIN',
    isLoggedIn: data.authenticated,
    pinVerified: data.verified_pin,
    passcodeVerified: data.verified_passcode,
    profileCompleted: data.profile_completed,
  });

  dispatch({ type: 'SELF_REGISTER_OPEN', selfRegisterOpen: data.self_register_open });
};

export const logout = async (dispatch: React.Dispatch<Action>) => {
  dispatch({ type: 'LOADING' });

  await fetch('api/login', {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  dispatch({ type: 'LOGOUT' });
};

export const forgetMe = async (dispatch: React.Dispatch<Action>) => {
  dispatch({ type: 'LOADING' });

  await fetch('api/forgetme', {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  dispatch({ type: 'FORGET_ME' });
};

export const showUsageTerms = async (show: boolean, dispatch: React.Dispatch<Action>) => {
  dispatch({ type: 'SHOW_USAGE_TERMS', showUsageTerms: show });
};
